import { useRef } from 'react';
import { cn } from '../../../lib/utils';
import { EYEBROW, FOCUS_RING, THEMES } from '../constants';
import { PanelHeading } from '../controls';
import { collectColors } from '../designUtils';

/** A theme as two-tone circle (compact) or a card with paper/accent/ink bars. */
export const ThemeButton = ({ theme, active, onApply, variant = 'card' }) =>
  variant === 'dot' ? (
    <button
      type="button"
      onClick={() => onApply(theme)}
      aria-label={theme.name}
      aria-pressed={active}
      title={theme.name}
      className={cn(
        'h-11 w-11 shrink-0 rounded-full overflow-hidden flex',
        active ? 'ring-2 ring-ink ring-offset-2 ring-offset-white' : 'border border-[#DCD6C8]',
        FOCUS_RING
      )}
      data-testid={`theme-${theme.id}`}
    >
      <span className="flex-1" style={{ background: theme.paper }} />
      <span className="flex-1" style={{ background: theme.accent }} />
    </button>
  ) : (
    <button
      type="button"
      onClick={() => onApply(theme)}
      aria-pressed={active}
      className={cn(
        'rounded-xl p-2.5 flex flex-col gap-2 text-left',
        active ? 'gradient-border-strong' : 'border-[1.5px] border-[#EEEBE4] bg-white hover:border-[#DCD6C8]',
        FOCUS_RING
      )}
      data-testid={`theme-${theme.id}`}
    >
      <span className="flex h-[26px] rounded-lg overflow-hidden border border-black/5" aria-hidden="true">
        <span className="flex-[2]" style={{ background: theme.paper }} />
        <span className="flex-1" style={{ background: theme.accent }} />
        <span className="flex-1" style={{ background: theme.ink }} />
      </span>
      <span className="text-xs font-extrabold text-ink">{theme.name}</span>
    </button>
  );

/** One detected colour; clicking opens the native picker to replace it everywhere. */
const DetectedColor = ({ entry, onReplace }) => {
  const from = useRef(entry.hex);
  const session = useRef('');
  const begin = () => {
    from.current = entry.hex;
    session.current = `recolor-${Date.now()}`;
  };
  return (
    <label
      className="relative h-[42px] rounded-xl border border-[#EEEBE4] bg-white flex items-center gap-2.5 px-2.5 cursor-pointer hover:border-[#DCD6C8] focus-within:ring-2 focus-within:ring-blue/50"
      onPointerDown={begin}
    >
      <span className="h-6 w-6 rounded-full border border-[#DCD6C8] shrink-0" style={{ background: entry.hex }} aria-hidden="true" />
      <span className="flex-1 min-w-0 text-[13px] font-bold text-ink truncate">{entry.where}</span>
      <span className="text-xs text-[#6B665C] font-mono">{entry.hex.toUpperCase()}</span>
      <input
        type="color"
        value={entry.hex}
        onFocus={begin}
        onChange={(e) => {
          if (!session.current) begin();
          onReplace(from.current, e.target.value, session.current);
          from.current = e.target.value;
        }}
        aria-label={`Change ${entry.hex} (${entry.where}) everywhere`}
        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
      />
    </label>
  );
};

/** Colours tool: one-click themes plus the colours used in this design. */
export const ColorsPanel = ({ design, themeId, onApplyTheme, onReplaceColor }) => {
  const colors = collectColors(design);
  return (
    <div className="flex flex-col gap-3.5">
      <PanelHeading>Colours</PanelHeading>
      <p className="text-[13px] leading-relaxed text-[#4A4640]">
        Recolour the whole design in one click: headings, names, small print, frames and ornaments change together.
      </p>
      <div className="grid grid-cols-2 gap-2">
        {THEMES.map((t) => (
          <ThemeButton key={t.id} theme={t} active={themeId === t.id} onApply={onApplyTheme} />
        ))}
      </div>
      <p className="text-xs text-[#6B665C] -mt-1">Background colour and effects: click the background of the invitation.</p>
      <p className={EYEBROW}>Colours in this design</p>
      <p className="text-xs text-[#6B665C] -mt-2">Click one to change it everywhere it&apos;s used.</p>
      <div className="flex flex-col gap-1.5" data-testid="detected-colors">
        {colors.length === 0 ? (
          <p className="text-sm text-[#6B665C]">No colours yet.</p>
        ) : (
          colors.map((c, i) => <DetectedColor key={i} entry={c} onReplace={onReplaceColor} />)
        )}
      </div>
    </div>
  );
};

export default ColorsPanel;
